/**
 * 媒体投放库页面 — MediaInsight（渠道池）：按品牌隔离展示已入库媒体。
 *
 * GET /api/media-delivery               投放库列表（来源/行业/地区/平台/状态筛选）
 * POST /api/media-delivery/auto-match   一键匹配信息源（引用源 → 总库频道自动入库）
 * POST /api/media-delivery/batch-remove 批量移除（勾选）
 * PATCH/DELETE /api/media-delivery/{id} 启停 / 移除
 *
 * 结构：4 指标卡 → 投放库列表（平台筛选 + 勾选批量移除 + 一键匹配信息源）。
 *
 * 投放库按品牌隔离（与引用源追溯一致）：列表按当前品牌 brand_id 加载，
 * 入库时绑定当前选中品牌/产品；未选品牌时展示空态提示。
 *
 * 绑定方向：文案 → 媒体。媒体投放库只是渠道池，不反绑文案；
 * 「哪篇文案 × 哪组媒体」由投放计划承载。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import {
  getMediaDelivery,
  autoMatchMediaDelivery,
  toggleMediaDelivery,
  removeMediaDelivery,
  batchRemoveMediaDelivery,
} from "../api/mediaPool";
import { useCompanyStore } from "../store/companyStore";
import {
  MEDIA_ADD_SOURCE_MAP,
  MEDIA_DELIVERY_STATUS_MAP,
  mediaTierBadgeConfig,
} from "../utils/constants";
import type { MediaDeliveryItem } from "../types";

/** 媒体等级徽章。 */
function TierBadge({ tier }: { tier?: string | null }) {
  const info = mediaTierBadgeConfig(tier);
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function MediaInsight() {
  const [items, setItems] = useState<MediaDeliveryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [snack, setSnack] = useState("");

  // 筛选
  const [filterSource, setFilterSource] = useState("");
  const [filterTaxonomy, setFilterTaxonomy] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [filterParent, setFilterParent] = useState("");

  // 平台/站点下拉选项（品牌切换时从全量加载一次，避免随筛选变化）
  const [siteOptions, setSiteOptions] = useState<string[]>([]);

  // 勾选批量移除
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removingBatch, setRemovingBatch] = useState(false);

  // 当前选中品牌/产品（切换品牌时重新加载投放库，投放库按品牌隔离，与引用源追溯一致）
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  // 一键匹配信息源（手动按钮）；自动匹配在首次空库时触发，每品牌仅一次
  const [autoMatching, setAutoMatching] = useState(false);
  const autoTriedRef = useRef<string>("");

  const flash = (msg: string) => {
    setSnack(msg);
    window.setTimeout(() => setSnack(""), 3000);
  };

  /** 加载投放库列表。 */
  const fetchList = useCallback(async () => {
    if (!selectedBrandId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    const params = {
      source: filterSource || undefined,
      taxonomy: filterTaxonomy || undefined,
      area: filterArea || undefined,
      parent_site: filterParent || undefined,
      brand_id: selectedBrandId,
    };
    try {
      let res = await getMediaDelivery(params);
      // 该品牌首次打开且投放库为空：自动一键匹配信息源（引用源 → 总库频道），
      // 与引用源追溯同源，保证投放库展示该品牌溯源匹配出的媒体。
      if ((!res || res.length === 0) && autoTriedRef.current !== selectedBrandId) {
        autoTriedRef.current = selectedBrandId;
        try {
          const m = await autoMatchMediaDelivery(selectedBrandId);
          if (m.domains_total > 0) {
            flash(
              m.added > 0
                ? `信息源自动匹配：溯源 ${m.domains_total} 源 → 新入库 ${m.added} 家媒体`
                : `溯源 ${m.domains_total} 源暂无匹配站点/行业，可点「一键匹配信息源」重试或手动添加`,
            );
          }
          res = await getMediaDelivery(params);
        } catch {
          // 自动匹配失败不阻塞列表展示（可手动重试）
        }
      }
      setItems(res || []);
    } catch {
      setItems([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterTaxonomy, filterArea, filterParent, selectedBrandId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // 平台/站点下拉选项：品牌切换时从全量加载一次
  useEffect(() => {
    if (!selectedBrandId) {
      setSiteOptions([]);
      return;
    }
    let cancelled = false;
    getMediaDelivery({ brand_id: selectedBrandId })
      .then((all) => {
        if (!cancelled) {
          setSiteOptions(
            Array.from(
              new Set(
                all
                  .map((i) => i.parent_site)
                  .filter((p): p is string => Boolean(p)),
              ),
            ).sort(),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setSiteOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBrandId]);

  /** 一键匹配信息源（手动重跑，幂等）。 */
  const handleManualAutoMatch = async () => {
    if (!selectedBrandId) return;
    setAutoMatching(true);
    try {
      const m = await autoMatchMediaDelivery(selectedBrandId);
      flash(
        `信息源匹配完成：溯源 ${m.domains_total} 源 → 新入库 ${m.added} 家媒体` +
          (m.duplicated ? `，已存在 ${m.duplicated} 家` : ""),
      );
      autoTriedRef.current = selectedBrandId;
      fetchList();
    } catch (e) {
      flash(e instanceof Error ? e.message : "一键匹配失败");
    } finally {
      setAutoMatching(false);
    }
  };

  /** 启停。 */
  const handleToggle = async (row: MediaDeliveryItem) => {
    const next = row.status === "active" ? "disabled" : "active";
    try {
      await toggleMediaDelivery(row.id, next);
      flash(`已${next === "active" ? "启用" : "停用"}「${row.name}」`);
      fetchList();
    } catch (e) {
      flash(e instanceof Error ? e.message : "操作失败");
    }
  };

  /** 移除。 */
  const handleRemove = async (row: MediaDeliveryItem) => {
    try {
      await removeMediaDelivery(row.id);
      flash(`已从投放库移除「${row.name}」`);
      fetchList();
    } catch (e) {
      flash(e instanceof Error ? e.message : "移除失败");
    }
  };

  /** 批量移除（勾选选中项）。 */
  const handleBatchRemove = async () => {
    if (selected.size === 0) return;
    setRemovingBatch(true);
    try {
      const res = await batchRemoveMediaDelivery(Array.from(selected));
      flash(`已从投放库移除 ${res.removed} 家媒体${res.skipped ? `，跳过 ${res.skipped} 家` : ""}`);
      setSelected(new Set());
      fetchList();
    } catch (e) {
      flash(e instanceof Error ? e.message : "批量移除失败");
    } finally {
      setRemovingBatch(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (items.length > 0 && items.every((i) => next.has(i.id))) {
        items.forEach((i) => next.delete(i.id));
      } else {
        items.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  const columns: DataTableColumn<MediaDeliveryItem>[] = [
    {
      key: "select",
      label: (
        <Checkbox
          size="small"
          checked={items.length > 0 && items.every((i) => selected.has(i.id))}
          onChange={toggleSelectAll}
        />
      ),
      render: (row) => (
        <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
      ),
    },
    {
      key: "name",
      label: "媒体 / 频道",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{row.name}</Typography>
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
            （{row.parent_site || "—"}）
          </Typography>
        </Box>
      ),
    },
    {
      key: "taxonomy",
      label: "行业频道",
      render: (row) => (
        <Typography sx={{ fontSize: 12 }}>{row.taxonomy || "—"}</Typography>
      ),
    },
    {
      key: "area",
      label: "地区",
      render: (row) => (
        <Typography sx={{ fontSize: 12 }}>{row.area || "—"}</Typography>
      ),
    },
    {
      key: "tier",
      label: "媒体等级",
      align: "center",
      render: (row) => <TierBadge tier={row.sort_type} />,
    },
    {
      key: "price",
      label: "价格",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {row.price != null ? `¥${row.price.toLocaleString()}` : "—"}
        </Typography>
      ),
    },
    {
      key: "source",
      label: "来源",
      align: "center",
      render: (row) => {
        const info = MEDIA_ADD_SOURCE_MAP[row.add_source] || {
          label: row.add_source,
          color: "text.secondary",
          bg: "semanticBg.neutral",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
    {
      key: "plan_count",
      label: "计入计划",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.plan_count}</Typography>
      ),
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => {
        const info = MEDIA_DELIVERY_STATUS_MAP[row.status] || {
          label: row.status,
          color: "text.secondary",
          bg: "semanticBg.neutral",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Button
            size="small"
            variant="outlined"
            sx={{ whiteSpace: "nowrap", py: 0.25 }}
            onClick={() => handleToggle(row)}
            color={row.status === "active" ? "warning" : "primary"}
          >
            {row.status === "active" ? "停用" : "启用"}
          </Button>
          <Button
            size="small"
            color="error"
            sx={{ whiteSpace: "nowrap", py: 0.25 }}
            onClick={() => handleRemove(row)}
          >
            移除
          </Button>
        </Box>
      ),
    },
  ];

  const activeCount = items.filter((i) => i.status === "active").length;
  const autoCount = items.filter((i) => i.add_source === "auto").length;
  const totalPrice = items.reduce((s, i) => s + (i.price || 0), 0);
  const coveredSiteCount = new Set(items.map((i) => i.parent_site).filter(Boolean)).size;

  // 未选品牌：展示空态（投放库按品牌隔离，与引用源追溯一致）
  if (!selectedBrandId) {
    return (
      <Box>
        <PageHeader
          title="媒体投放库"
          breadcrumbs={[{ label: "媒体投放" }, { label: "媒体投放库" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先在顶栏选择品牌"
              subtitle="投放库按品牌隔离（与引用源追溯一致），选择品牌后自动加载该品牌的获投媒体"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="媒体投放库"
        breadcrumbs={[{ label: "媒体投放" }, { label: "媒体投放库" }]}
        subtitle="信息源从媒体总库匹配出的获投媒体 · 渠道池 · 供投放计划选择媒体"
      />

      {/* 指标卡 ×4 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="投放库媒体" value={loading ? "—" : items.length} subtitle="渠道池 · 获投媒体" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="启用媒体"
            value={loading ? "—" : activeCount}
            subtitle="可投放"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="信息源匹配" value={loading ? "—" : autoCount} subtitle="自动匹配入库" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="覆盖站点"
            value={loading ? "—" : coveredSiteCount}
            subtitle={`预估 ¥${totalPrice.toLocaleString()}`}
          />
        </Grid>
      </Grid>

      {/* 投放库列表 */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>已入库媒体（投放库）</Typography>
          <Badge label={`${items.length} 个`} variant="info" />
          {selected.size > 0 && (
            <>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                已选 {selected.size} 项
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={removingBatch}
                onClick={handleBatchRemove}
                sx={{ mr: 1 }}
              >
                {removingBatch ? "移除中..." : "批量移除"}
              </Button>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            disabled={autoMatching}
            onClick={handleManualAutoMatch}
          >
            {autoMatching ? "匹配中..." : "一键匹配信息源"}
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="来源"
            select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="">全部来源</MenuItem>
            <MenuItem value="auto">信息源匹配</MenuItem>
            <MenuItem value="manual">手动添加</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="行业频道"
            value={filterTaxonomy}
            onChange={(e) => setFilterTaxonomy(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="地区"
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="平台/站点"
            select
            value={filterParent}
            onChange={(e) => setFilterParent(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="">全部平台</MenuItem>
            {siteOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {loading ? (
          <Loading fullPage />
        ) : failed ? (
          <Card>
            <CardContent>
              <EmptyState text="媒体投放库暂未接入" source="信息源匹配后一键入库，或手动从总库添加" />
            </CardContent>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            showPagination={false}
            getRowId={(row) => row.id}
            emptyText="暂无获投媒体，请先信息源匹配或从总库添加"
          />
        )}
      </Box>

      {/* 口径说明 */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            <b style={{ color: "text.primary" }}>媒体投放库是渠道池</b>：只维护「我们要投哪些媒体」，不绑定文案。
            真正把<b style={{ color: "text.primary" }}>文案 × 媒体</b>绑定起来的是投放计划——由文案库选一篇文案，从本库选一组媒体，组成一个投放计划（含排期与金额）。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
